import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { post, buildUrl } from "../../lib/request"

export const registerWithPassword = createAsyncThunk(
  "registerPassword/register",
  async (
    { email, password, captchaToken, captchaSiteKey, token, username, fullName },
    { rejectWithValue }
  ) => {
    try {
      /* ---------------- REGISTER ---------------- */
      const registerUrl = token
        ? buildUrl(`/authn/register/user?token=${encodeURIComponent(token)}`)
        : buildUrl("/authn/register/user")

      const registerResponse = await post(
        registerUrl,
        {
          email,
          username: username || email,
          password,
          captchaToken,
          fullName: fullName || "",
        },
        { skipAuth: true }
      )

      /* ---------------- LOGIN ---------------- */
      const loginResponse = await post(
        buildUrl("/authn/login"),
        {
          username: email,
          password,
        },
        { skipAuth: true }
      )

      const accessToken = loginResponse?.data?.accessToken
      if (!accessToken) {
        throw new Error("No access token received from login")
      }

      const user = { username: email }

      try {
        localStorage.setItem("auth_token", accessToken)
        localStorage.setItem("auth_user", JSON.stringify(user))
      } catch (e) {
        console.error("Failed to store token", e)
      }

      return {
        registerData: registerResponse,
        loginData: loginResponse,
        token: accessToken,
        user,
      }
    } catch (error) {
      const code = error?.code
      const message = error?.message || "Failed to register."
      return rejectWithValue(code != null || message ? { code, message } : { message })
    }
  }
)

const initialState = {
  status: "idle",
  error: null,
  data: null,
  success: false,
  loading: false,
  token: null,
  user: null,
}

const registerPasswordSlice = createSlice({
  name: "registerPassword",
  initialState,
  reducers: {
    clearRegisterPasswordState: (state) => {
      state.status = "idle"
      state.error = null
      state.data = null
      state.success = false
      state.loading = false
      state.token = null
      state.user = null
    },
    clearRegisterPasswordError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerWithPassword.pending, (state) => {
        state.status = "loading"
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(registerWithPassword.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.loading = false
        state.data = action.payload
        state.success = true
        state.token = action.payload.token
        state.user = action.payload.user
      })
      .addCase(registerWithPassword.rejected, (state, action) => {
        state.status = "failed"
        state.loading = false
        state.error =
          action.payload && typeof action.payload === "object"
            ? action.payload
            : { message: action.payload || "Failed to register password" }
        state.success = false
      })
  },
})

export const {
  clearRegisterPasswordState,
  clearRegisterPasswordError,
} = registerPasswordSlice.actions

export default registerPasswordSlice.reducer
